import * as z from "zod";

// Input schema for Try First analytics
export const GetTryFirstAnalyticsInputSchema = z.object({
  startDate: z
    .string()
    .describe("Start date for the analytics period (YYYY-MM-DD)"),
  endDate: z
    .string()
    .describe("End date for the analytics period (YYYY-MM-DD)"),
});

// Schema for profitability metrics with comparison values
const ProfitabilityMetricSchema = z.object({
  combined: z.number().describe("Combined value for all orders"),
  tryFirst: z.number().describe("Value for Try First orders only"),
  nonTryFirst: z.number().describe("Value for non-Try First orders"),
});

// Schema for daily order data point
const OrderDataPointSchema = z.object({
  date: z.string().describe("Date for this data point (YYYY-MM-DD)"),
  orders: z.number().describe("Number of Try First orders on this date"),
});

// Product performance schema
const ProductPerformanceSchema = z.object({
  productId: z.string().describe("Product ID"),
  productName: z.string().describe("Product name"),
  productImageUrl: z.string().optional().describe("Product image URL"),
  variantId: z.string().optional().describe("Variant ID if applicable"),
  variantName: z.string().optional().describe("Variant name if applicable"),
  tryFirstOrders: z
    .number()
    .describe("Number of Try First orders for this product"),
  totalOrders: z.number().describe("Total orders for this product"),
  tryFirstReturnRate: z
    .number()
    .describe("Return rate for Try First orders (0-1)"),
  regularReturnRate: z
    .number()
    .describe("Return rate for regular orders (0-1)"),
});

// Output schema for Try First analytics
export const GetTryFirstAnalyticsOutputSchema = z.object({
  keyMetrics: z.object({
    totalOrders: z
      .number()
      .describe("Total number of Try First orders in the period"),
    totalOrderValue: z.number().describe("Total value of Try First orders"),
    totalRetainedRevenue: z
      .number()
      .describe("Total revenue retained through exchanges and store credit"),
    shopperOptInRate: z
      .number()
      .describe(
        "Shopper opt-in rate (Try First orders / Try First eligible orders)",
      ),
  }),
  ordersOverTime: z
    .array(OrderDataPointSchema)
    .describe("Daily breakdown of Try First orders"),
  profitabilityMetrics: z.object({
    grossAov: ProfitabilityMetricSchema.describe(
      "Gross Average Order Value comparison",
    ),
    averageCartSize: ProfitabilityMetricSchema.describe(
      "Average Units Per Transaction comparison",
    ),
    returnRate: ProfitabilityMetricSchema.describe(
      "Return rate comparison (percentage)",
    ),
  }),
  products: z
    .array(ProductPerformanceSchema)
    .describe("Product-level Try First performance"),
});

// Export type definitions for TypeScript
export type GetTryFirstAnalyticsInput = z.infer<
  typeof GetTryFirstAnalyticsInputSchema
>;
export type GetTryFirstAnalyticsOutput = z.infer<
  typeof GetTryFirstAnalyticsOutputSchema
>;

// Define the entire schema for RPC usage
export const getTryFirstAnalytics = {
  name: "getTryFirstAnalytics" as const,
  input: GetTryFirstAnalyticsInputSchema,
  output: GetTryFirstAnalyticsOutputSchema,
};
